import { AgeGroup, getAgeGroup } from '@repo/utils';
import bacteroidesCharacter from 'assets/images/summary/first-look/bacteroides_character.svg';
import bifidobacteriumCharacter from 'assets/images/summary/first-look/bifidobacterium_character.svg';
import bifidobacteriumBabyCharacter from 'assets/images/summary/first-look/character_baby_1.svg';
import enterobacteriaceaeCharacter from 'assets/images/summary/first-look/enterobacteriaceae_character.svg';
import lachnospiraceaeCharacter from 'assets/images/summary/first-look/lachnospiraceae_character.svg';
import otherCharacter from 'assets/images/summary/first-look/other_character.svg';
import prevotellaCharacter from 'assets/images/summary/first-look/prevotella_character.svg';
import ruminococcaceaeCharacter from 'assets/images/summary/first-look/ruminococcaceae_character.svg';
import { getGutTypeLookupBaby } from './gutTypesBaby';

type GutTypeKey =
  | 'lachnospiraceae'
  | 'bacteroides'
  | 'prevotella'
  | 'bifidobacterium'
  | 'ruminococcaceae'
  | 'enterobacteriaceae'
  | 'other';

export interface GutTypeValue {
  type: string;
  characterImage: string;
  shortDescription: string;
  longDescription: string;
  extraBackgroundClass?: string;
}

const GUT_TYPES: Record<GutTypeKey, GutTypeValue> = {
  lachnospiraceae: {
    type: 'Lachnospiraceae',
    characterImage: lachnospiraceaeCharacter,
    shortDescription: 'Fiber digesting superhero',
    longDescription:
      'Your gut microbiome is rich in microbes that are great at digesting fibers such as starch, inulin, and pectin.',
  },
  bacteroides: {
    type: 'Bacteroides',
    characterImage: bacteroidesCharacter,
    shortDescription: 'The Protein & Fat Superprocessor',
    longDescription:
      'Your gut is rich in versatile digestive powerhouses that efficiently break down proteins and fats from meat and dairy-rich diets.',
  },
  prevotella: {
    type: 'Prevotella',
    characterImage: prevotellaCharacter,
    shortDescription: 'The Plant Power Squad',
    longDescription:
      'Your gut is rich in veggie-loving heroes that thrive on fiber rich diets and complex carbohydrates.',
  },
  ruminococcaceae: {
    type: 'Ruminococcaceae',
    characterImage: ruminococcaceaeCharacter,
    shortDescription: 'The Butyrate Builders',
    longDescription:
      'Rich in anti-inflammatory warriors that keep your intestinal lining strong and happy.',
  },
  bifidobacterium: {
    type: 'Bifidobacterium',
    characterImage: bifidobacteriumCharacter,
    shortDescription: 'The Disrupted Gut',
    longDescription:
      'While good in small doses, it can be a sign of a disrupted gut for those above 3 years old. This is common after antibiotics or inflammation.',
  },
  enterobacteriaceae: {
    type: 'Enterobacteriaceae',
    characterImage: enterobacteriaceaeCharacter,
    shortDescription: 'The Opportunistic Wildcards',
    longDescription:
      'Your gut is rich in unpredictable antiheroes who can be helpful in small numbers but problematic when they overgrow.',
  },
  other: {
    type: 'Other',
    characterImage: otherCharacter,
    shortDescription: 'The Diverse Alliance',
    longDescription:
      'Your gut is rich in a supporting cast of countless bacterial species that fill niche roles and add resilience to your microbiome.',
  },
};

const OVERRIDE_GUT_TYPES_TODDLER: Partial<Record<GutTypeKey, GutTypeValue>> = {
  bifidobacterium: {
    type: 'Bifidobacterium',
    characterImage: bifidobacteriumBabyCharacter,
    shortDescription: 'The Developing Gut',
    longDescription:
      'While the gut is developing its ability to break down complex fibers, your gut is still rich in Bifidobacterium that digest simple sugars (like those in breastmilk)',
  },
};

export function getGutType(key: string, age: number) {
  const babyGutTypes = getGutTypeLookupBaby(age);
  const types: Record<string, GutTypeValue> = { ...GUT_TYPES, ...babyGutTypes };

  const ageGroup = getAgeGroup(age);
  if (ageGroup === AgeGroup.TODDLER || ageGroup === AgeGroup.BABY) {
    Object.assign(types, OVERRIDE_GUT_TYPES_TODDLER);
  }

  const found = types[key];
  if (!found) {
    console.error(`No gut type with key "${key}" found, using default.`);
    return GUT_TYPES.lachnospiraceae;
  }

  return found;
}
